import { differenceInMinutes } from 'date-fns';
import { MONTHS, dayKey, durationLabel, isSameDay, sentenceCase, weekOf } from './agendaDay';
import { CALENDAR_END_HOUR, CALENDAR_START_HOUR } from './outOfHours';

// Regras da Semana (Agenda no celular, aba Semana de Horários), sem React e sem relógio. Horários em minutos desde 00:00,
// no fuso do navegador, como o calendário e a Lista do dia

// Medidas da grade, em px (o CSS recebe as que variam pelas variáveis do componente)
export const WEEK_GUTTER = 44;       // régua das horas, presa à esquerda
export const HOUR_HEIGHT = 104;      // 26 px por 15 min: o bloco de 15 min fica com 24 px de alvo (WCAG 2.5.8)
export const LANE_MIN = 70;          // largura mínima de cada doutor em "Todos"
export const SINGLE_LANE_MIN = 98;   // largura mínima do dia com um doutor só
export const NARROW_LANE = 92;       // até esta largura, o bloco mostra só o primeiro nome
export const SLOT_MINUTES = 15;      // horário marcado pelo toque (a duração mais comum na agenda)

const DAY_START = CALENDAR_START_HOUR * 60;
const DAY_END = CALENDAR_END_HOUR * 60;
const MONTHS_SHORT = MONTHS.map((month) => month.slice(0, 3));

export const minutesOf = (date) => date.getHours() * 60 + date.getMinutes();
const toMinutes = (time) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

// Horários de atendimento do doutor num dia da semana ([início, fim] em minutos). [] = não atende; null = sem a informação
// (doutor fora da lista, ou lista sem os horários)
export function servicePeriods(doctor, weekDay) {
  if (!Array.isArray(doctor?.service_hours)) return null;
  return doctor.service_hours
    .filter((hour) => hour.week_day === weekDay)
    .map((hour) => [toMinutes(hour.start_time), toMinutes(hour.end_time)]);
}

// Início e fim do agendamento dentro do dia dele (o que passa da meia-noite termina às 24:00)
export function itemMinutes(item) {
  const start = minutesOf(item.start);
  const end = isSameDay(item.start, item.end) ? minutesOf(item.end) : 24 * 60;
  return [start, Math.max(end, start)];
}

const itemsOfDay = (byDay, date, laneIds) => (byDay.get(dayKey(date)) ?? []).filter((item) => laneIds.includes(item.tecnico?.id));

// Dias da grade: segunda a sexta. Sábado e domingo entram com agendamento (ou bloqueio) dos doutores das colunas, quando
// um deles atende nesse dia, ou quando são o dia escolhido
export function weekColumns(day, byDay, laneIds, doctorsById) {
  return weekOf(day).filter((date) => {
    const weekDay = date.getDay();
    if (weekDay >= 1 && weekDay <= 5) return true;
    if (isSameDay(date, day)) return true;
    if (itemsOfDay(byDay, date, laneIds).length) return true;
    return laneIds.some((id) => (servicePeriods(doctorsById.get(id), weekDay) ?? []).length > 0);
  });
}

// Horas da grade: da hora cheia do começo mais cedo à hora cheia do fim mais tarde, contando o atendimento dos doutores das
// colunas e os agendamentos dos dias da grade, sempre entre 07:00 e 20:00 (a régua do calendário). Sem nada: 08:00 às 18:00
export function hourRange(days, byDay, laneIds, doctorsById) {
  let start = Infinity;
  let end = -Infinity;
  const include = (from, to) => {
    start = Math.min(start, from);
    end = Math.max(end, to);
  };
  days.forEach((date) => {
    laneIds.forEach((id) => (servicePeriods(doctorsById.get(id), date.getDay()) ?? []).forEach(([from, to]) => include(from, to)));
    itemsOfDay(byDay, date, laneIds).forEach((item) => include(...itemMinutes(item)));
  });
  if (!Number.isFinite(start)) return { start: 8 * 60, end: 18 * 60 };

  const first = Math.min(DAY_END - 60, Math.max(DAY_START, Math.floor(start / 60) * 60));
  const last = Math.max(first + 60, Math.min(DAY_END, Math.ceil(end / 60) * 60));
  return { start: first, end: last };
}

// Largura do dia: o mínimo pelos doutores ou a tela dividida pelos dias, o que for maior (sobrando espaço, como deitado e
// no tablet, a semana inteira cabe). width = largura da grade, com a régua
export function dayWidth(width, dayCount, laneCount) {
  const min = laneCount > 1 ? laneCount * LANE_MIN + (laneCount - 1) : SINGLE_LANE_MIN;
  if (!dayCount || !(width > WEEK_GUTTER)) return min;
  // Centésimos para baixo (a soma não passa da tela e não cria rolagem de 1 px); a folga evita 160,2 virar 160,19
  return Math.max(min, Math.floor(((width - WEEK_GUTTER) / dayCount) * 100 + 1e-6) / 100);
}

// Posição vertical de um horário na grade
export const minuteTop = (minutes, range) => ((minutes - range.start) * HOUR_HEIGHT) / 60;

// Caixa do bloco: topo e altura dentro da grade (1 px de folga em cima e embaixo). Agendamento fora das horas da grade
// (antes das 07:00, depois das 20:00) fica na borda, com a altura do bloco de 15 min, para continuar tocável
export function blockBox(item, range) {
  const [start, end] = itemMinutes(item);
  const from = Math.min(Math.max(start, range.start), range.end - SLOT_MINUTES);
  const to = Math.max(Math.min(end, range.end), from + SLOT_MINUTES);
  return { top: minuteTop(from, range) + 1, height: minuteTop(to, range) - minuteTop(from, range) - 2 };
}

// Encaixe no mesmo doutor (não acontece hoje: o servidor recusa, e o horário liberado pode ficar por baixo): lado a lado
// dentro da coluna dele. Devolve { item, col, cols }, na ordem de início
export function laneLayout(items) {
  const out = [];
  let group = [];
  let groupEnd = -1;
  const flush = () => {
    const ends = [];
    group.forEach((item) => {
      const [start, end] = itemMinutes(item);
      let col = ends.findIndex((last) => last <= start);
      if (col === -1) {
        col = ends.length;
        ends.push(end);
      } else ends[col] = end;
      out.push({ item, col });
    });
    for (let index = out.length - group.length; index < out.length; index += 1) out[index].cols = ends.length;
    group = [];
    groupEnd = -1;
  };
  [...items]
    .sort((a, b) => (a.start - b.start) || (b.end - a.end))
    .forEach((item) => {
      const [start, end] = itemMinutes(item);
      if (group.length && start >= groupEnd) flush();
      group.push(item);
      groupEnd = Math.max(groupEnd, end);
    });
  if (group.length) flush();
  return out;
}

// Horário tocado na coluna: o quarto de hora em que o toque caiu (arredonda para baixo), ou null fora da grade
export function slotAt(offsetY, range) {
  if (!(offsetY >= 0)) return null;
  const minutes = range.start + Math.floor((offsetY * 60) / HOUR_HEIGHT / SLOT_MINUTES) * SLOT_MINUTES;
  return minutes < range.end ? minutes : null;
}

// O horário marcado pelo toque: 'out-of-hours' (cai numa faixa "Fora do horário"), 'taken' (um agendamento que ocupa) ou
// 'free'. bands: [início, fim] do doutor no dia; occupying: os agendamentos dele no dia que ocupam o horário
export function slotState(start, bands, occupying) {
  const end = start + SLOT_MINUTES;
  if (bands.some(([from, to]) => start < to && from < end)) return 'out-of-hours';
  if (occupying.some((item) => { const [from, to] = itemMinutes(item); return start < to && from < end; })) return 'taken';
  return 'free';
}

// Faixas "Fora do horário" (as do calendário: outOfHoursEvents) por doutor e dia: 'id|yyyy-MM-dd' -> [[início, fim]]
export function bandsByLane(outOfHours) {
  const lanes = new Map();
  outOfHours.forEach((band) => {
    const key = `${band.resourceId}|${dayKey(band.start)}`;
    if (!lanes.has(key)) lanes.set(key, []);
    lanes.get(key).push(itemMinutes(band));
  });
  return lanes;
}

// Faixas recortadas pelas horas da grade
export const clipBands = (bands, range) => bands
  .map(([from, to]) => [Math.max(from, range.start), Math.min(to, range.end)])
  .filter(([from, to]) => to > from);

// Nome do paciente no bloco: o primeiro e o último nome; numa coluna estreita, só o primeiro
export function blockName(nome, narrow) {
  const words = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2 || narrow) return words[0] ?? '';
  return `${words[0]} ${words[words.length - 1]}`;
}

// O que cabe no bloco pela duração: 15 min, só o nome; 30 min, também o horário; 45 min, o tipo; 1 h ou mais, o progresso
export const blockLines = (minutes) => ({ time: minutes >= 30, type: minutes >= 45, progress: minutes >= 60 });

// Nome acessível do bloco: o mesmo texto do cartão da Lista ("08:00 até 08:15, ANA SOUZA, Pacote, com BRUNO,
// Atendimentos: 3/10, status REALIZADO"), porque o bloco mostra só um pedaço
export function blockLabel(item, kind, doctorName) {
  const duration = differenceInMinutes(item.end, item.start) >= 60 ? durationLabel(item.start, item.end) : '';
  const progress = kind !== 'block' ? item.prontuario?.progresso : '';
  return [
    `${item.hora_inicial} até ${item.hora_final}`,
    duration,
    kind === 'block' ? 'Horário bloqueado' : item.paciente?.nome,
    kind === 'block' ? 'Bloqueio' : sentenceCase(item.tipo_atendimento?.descricao),
    `com ${doctorName}`,
    progress ? `Atendimentos: ${progress}` : '',
    kind === 'released' ? 'horário liberado' : '',
    `status ${item.status?.nome ?? 'Sem status'}`,
  ].filter(Boolean).join(', ');
}

// "12 a 16 de outubro", "28 de setembro a 2 de outubro"; o ano aparece quando não é o de hoje (nas duas pontas, se cruza o ano)
export function weekTitle(first, last, today) {
  const suffix = (date) => (date.getFullYear() !== today.getFullYear() ? ` de ${date.getFullYear()}` : '');
  const end = `${last.getDate()} de ${MONTHS[last.getMonth()]}`;
  if (first.getFullYear() !== last.getFullYear()) {
    return `${first.getDate()} de ${MONTHS[first.getMonth()]} de ${first.getFullYear()} a ${end} de ${last.getFullYear()}`;
  }
  if (first.getMonth() !== last.getMonth()) return `${first.getDate()} de ${MONTHS[first.getMonth()]} a ${end}${suffix(last)}`;
  return `${first.getDate()} a ${end}${suffix(last)}`;
}

// Mês da semana no canto da grade: "out", ou "set/out" quando a semana cruza o mês
export const weekMonths = (days) => [...new Set(days.map((date) => MONTHS_SHORT[date.getMonth()]))].join('/');

// Texto preto ou branco sobre a cor do status: o que tiver mais contraste pela fórmula da WCAG (o calendário usa o brilho
// YIQ, que deixa texto branco com 2,6:1 no REALIZADO). Cor inválida: preto
function channels(hex) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!match) return null;
  const value = match[1].length === 3 ? match[1].split('').map((char) => char + char).join('') : match[1];
  return [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
}

export function textOn(hex) {
  const rgb = channels(hex);
  if (!rgb) return '#000000';
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? '#000000' : '#ffffff';
}

// A cor do status com transparência, para as listras do bloqueio
export function withAlpha(hex, alpha) {
  const rgb = channels(hex) ?? [173, 181, 189];
  return `rgba(${rgb.join(', ')}, ${alpha})`;
}
